/**
 * Structured API debug logger for all axios instances.
 * Logs full request and response details to the browser console.
 *
 * Usage:
 *   import { attachApiLogger } from '@/shared/apiLogger'
 *   attachApiLogger(axiosInstance, 'ServiceName')
 */

function formatHeaders(headers) {
  if (!headers) return {}
  // axios wraps headers in an AxiosHeaders object; spread to plain object
  return typeof headers.toJSON === 'function' ? headers.toJSON() : { ...headers }
}

function buildFullUrl(config) {
  return config.baseURL
    ? `${config.baseURL.replace(/\/$/, '')}/${(config.url || '').replace(/^\//, '')}`
    : config.url
}

function logRequest(service, config) {
  const method = (config.method || 'get').toUpperCase()
  const url = buildFullUrl(config)

  // Structured entry for log export (captured by consoleLogger)
  console.log(`[${service}] ➤ ${method} ${config.url}`, {
    type: 'api-request',
    service,
    method,
    url,
    path: config.url,
    headers: formatHeaders(config.headers),
    params: config.params || null,
    body: config.data || null,
    timeout: config.timeout,
    withCredentials: config.withCredentials
  })

  // Pretty browser console output
  console.groupCollapsed(
    `%c[${service}] %c${method} %c${config.url}`,
    'color: #8B5CF6; font-weight: bold;',
    'color: #F59E0B; font-weight: bold;',
    'color: #3B82F6;'
  )
  console.log('%cFull URL:', 'font-weight: bold;', url)
  console.log('%cMethod:', 'font-weight: bold;', method)
  console.log('%cHeaders:', 'font-weight: bold;', formatHeaders(config.headers))
  if (config.params) {
    console.log('%cParams:', 'font-weight: bold;', config.params)
  }
  if (config.data) {
    console.log('%cRequest Body:', 'font-weight: bold;', config.data)
  }
  console.log('%cTimeout:', 'font-weight: bold;', config.timeout)
  console.log('%cWith Credentials:', 'font-weight: bold;', config.withCredentials)
  console.groupEnd()
}

function logResponse(service, response, durationMs) {
  const method = (response.config.method || 'get').toUpperCase()
  const status = response.status
  const statusColor = status < 300 ? '#10B981' : status < 400 ? '#F59E0B' : '#EF4444'

  // Structured entry for log export (captured by consoleLogger)
  console.log(`[${service}] ✔ ${method} ${response.config.url} ${status} (${durationMs}ms)`, {
    type: 'api-response',
    service,
    method,
    path: response.config.url,
    url: buildFullUrl(response.config),
    status,
    statusText: response.statusText,
    durationMs,
    responseHeaders: formatHeaders(response.headers),
    responseData: response.data
  })

  // Pretty browser console output
  console.groupCollapsed(
    `%c[${service}] %c${method} %c${response.config.url} %c${status} ${response.statusText} %c(${durationMs}ms)`,
    'color: #8B5CF6; font-weight: bold;',
    'color: #F59E0B; font-weight: bold;',
    'color: #3B82F6;',
    `color: ${statusColor}; font-weight: bold;`,
    'color: #6B7280;'
  )
  console.log('%cStatus:', 'font-weight: bold;', `${status} ${response.statusText}`)
  console.log('%cResponse Headers:', 'font-weight: bold;', formatHeaders(response.headers))
  console.log('%cResponse Data:', 'font-weight: bold;', response.data)
  console.log('%cDuration:', 'font-weight: bold;', `${durationMs}ms`)
  console.groupEnd()
}

function logError(service, error, durationMs) {
  const config = error.config || {}
  const method = (config.method || 'get').toUpperCase()
  const response = error.response

  // Structured entry for log export (captured by consoleLogger)
  console.error(`[${service}] ✗ ${method} ${config.url} ${response ? response.status : 'NETWORK ERROR'} (${durationMs}ms)`, {
    type: 'api-error',
    service,
    method,
    path: config.url,
    url: buildFullUrl(config),
    errorMessage: error.message,
    status: response ? response.status : null,
    statusText: response ? response.statusText : null,
    durationMs,
    responseData: response ? response.data : null,
    responseHeaders: response ? formatHeaders(response.headers) : null,
    requestHeaders: formatHeaders(config.headers),
    params: config.params || null,
    body: config.data || null
  })

  // Pretty browser console output
  console.groupCollapsed(
    `%c[${service}] %c${method} %c${config.url} %c✗ ${response ? response.status : 'NETWORK ERROR'} %c(${durationMs}ms)`,
    'color: #8B5CF6; font-weight: bold;',
    'color: #F59E0B; font-weight: bold;',
    'color: #3B82F6;',
    'color: #EF4444; font-weight: bold;',
    'color: #6B7280;'
  )
  console.log('%cError Message:', 'font-weight: bold;', error.message)
  if (response) {
    console.log('%cStatus:', 'font-weight: bold;', `${response.status} ${response.statusText}`)
    console.log('%cResponse Headers:', 'font-weight: bold;', formatHeaders(response.headers))
    console.log('%cResponse Data:', 'font-weight: bold;', response.data)
  } else {
    console.log('%cNo response received (network error / timeout)')
  }
  console.log('%cRequest Config:', 'font-weight: bold;', {
    url: config.url,
    baseURL: config.baseURL,
    method: method,
    headers: formatHeaders(config.headers),
    params: config.params,
    data: config.data,
    timeout: config.timeout
  })
  console.log('%cDuration:', 'font-weight: bold;', `${durationMs}ms`)
  console.groupEnd()
}

/**
 * Attach structured debug logging interceptors to an axios instance.
 * @param {import('axios').AxiosInstance} instance
 * @param {string} serviceName - Label shown in console (e.g. 'API', 'Media', 'Integrations')
 */
export function attachApiLogger(instance, serviceName) {
  // Request interceptor — stamp start time
  instance.interceptors.request.use(
    (config) => {
      config._apiLogStart = Date.now()
      logRequest(serviceName, config)
      return config
    },
    (error) => {
      console.error(`%c[${serviceName}] Request setup error:`, 'color: #EF4444; font-weight: bold;', error)
      return Promise.reject(error)
    }
  )

  // Response interceptor — log success and error
  instance.interceptors.response.use(
    (response) => {
      const duration = response.config._apiLogStart ? Date.now() - response.config._apiLogStart : 0
      logResponse(serviceName, response, duration)
      return response
    },
    (error) => {
      const config = error.config || {}
      const duration = config._apiLogStart ? Date.now() - config._apiLogStart : 0
      logError(serviceName, error, duration)
      return Promise.reject(error)
    }
  )
}
